import React, { useState } from "react";
import { Plus, Trash2, Save, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { fmtNum } from "../../lib/format";

const EMPTY = { description: "", quantity: 1, unit: "pcs", unit_price: 0, vat_rate: 2.6, restock: false };

const LineRow = ({ ln, idx, onChange, onRemove }) => {
  const set = (k) => (e) => onChange(idx, { ...ln, [k]: e.target.value });
  return (
    <TableRow data-testid={`cn-edit-line-${idx}`}>
      <TableCell><Input value={ln.description} onChange={set("description")} data-testid={`cn-edit-desc-${idx}`} /></TableCell>
      <TableCell><Input type="number" step="0.01" value={ln.quantity} onChange={set("quantity")} className="text-right" data-testid={`cn-edit-qty-${idx}`} /></TableCell>
      <TableCell><Input value={ln.unit} onChange={set("unit")} data-testid={`cn-edit-unit-${idx}`} /></TableCell>
      <TableCell><Input type="number" step="0.01" value={ln.unit_price} onChange={set("unit_price")} className="text-right" data-testid={`cn-edit-price-${idx}`} /></TableCell>
      <TableCell><Input type="number" step="0.1" value={ln.vat_rate} onChange={set("vat_rate")} className="text-right" data-testid={`cn-edit-vat-${idx}`} /></TableCell>
      <TableCell className="text-right font-mono">{fmtNum((Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0))}</TableCell>
      <TableCell className="text-center">
        <Checkbox
          checked={!!ln.restock}
          disabled={!ln.product_id}
          onCheckedChange={(v) => onChange(idx, { ...ln, restock: !!v })}
          data-testid={`cn-edit-restock-${idx}`}
        />
      </TableCell>
      <TableCell>
        <Button size="sm" variant="ghost" onClick={() => onRemove(idx)} data-testid={`cn-edit-remove-${idx}`}>
          <Trash2 className="w-4 h-4 text-red-600" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

export const CreditNoteLinesEditor = ({ initialLines, onSave, onCancel, busy }) => {
  const [lines, setLines] = useState(() => (initialLines || []).map((l) => ({ ...l })));
  const update = (idx, ln) => setLines((ls) => ls.map((l, i) => (i === idx ? ln : l)));
  const remove = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));
  const valid = lines.length > 0 && lines.every((l) => (l.description || "").trim());
  const save = () => onSave(lines.map((l) => ({
    ...l,
    quantity: Number(l.quantity) || 0,
    unit_price: Number(l.unit_price) || 0,
    vat_rate: Number(l.vat_rate) || 0,
  })));

  return (
    <div className="space-y-3" data-testid="cn-lines-editor">
      <Table>
        <TableHeader>
          <TableRow className="bg-[#FAFAF7]">
            <TableHead>Description</TableHead>
            <TableHead className="w-24 text-right">Qté</TableHead>
            <TableHead className="w-20">Unité</TableHead>
            <TableHead className="w-28 text-right">P.U.</TableHead>
            <TableHead className="w-20 text-right">TVA %</TableHead>
            <TableHead className="w-28 text-right">Sous-total</TableHead>
            <TableHead className="w-16 text-center">Restock</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((ln, idx) => <LineRow key={ln.id || `new-${idx}`} ln={ln} idx={idx} onChange={update} onRemove={remove} />)}
        </TableBody>
      </Table>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => setLines((ls) => [...ls, { ...EMPTY }])} data-testid="cn-edit-add-line">
          <Plus className="w-4 h-4 mr-1" /> Ajouter une ligne
        </Button>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy} data-testid="cn-edit-cancel">
            <X className="w-4 h-4 mr-1" /> Annuler
          </Button>
          <Button size="sm" onClick={save} disabled={busy || !valid} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="cn-edit-save">
            <Save className="w-4 h-4 mr-1" /> Enregistrer les lignes
          </Button>
        </div>
      </div>
      <p className="text-[11px] text-[#575A57]">Les totaux (HT, TVA, arrondi 0.05) sont recalculés à l&apos;enregistrement. « Restock » n&apos;est possible que sur une ligne liée à un produit.</p>
    </div>
  );
};

export default CreditNoteLinesEditor;
